import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  HrFlexContainer,
  HrTitleContainer,
  HrTitleH4,
  HrParagraph,
  HrButtons,
  HrlightestGrey,
  HrlighterGrey,
  HrGreen,
  HrGreenHover,
  HrFontBold,
  HrBlockContainer
} from '@components/styles/hr-styles';
import { Checkbox } from '@components/checkbox/checkbox';
import { Modal } from '../modal';
import useStore from '../../store/useStore';
import { DisplaySettingsContext } from '../../store/display-settings';

const LegalText = styled(HrBlockContainer)`
  overflow-y: auto;
  max-height: 320px;
`;

const LegalHeading = styled(HrParagraph)`
  font-weight: ${HrFontBold};
  margin-top: 12px !important;
`;

const LegalLink = styled.a`
  color: ${HrGreen};
  word-break: break-all;

  &:hover {
    color: ${HrGreenHover};
  }
`;

const LegalBulletList = styled.ul`
  margin: 8px 0 0 0 !important;
  padding-left: 20px;
`;

export const LegalConsent = ({ onAuthorize }) => {
  const { getDataPostulant, vacancy } = useStore();
  const [t] = useTranslation();
  const { darkMode } = useContext(DisplaySettingsContext);
  const [accepted, setAccepted] = useState(false);
  const isConfidentialVacancy = vacancy?.status === 6;

  const deny = () => {
    window.location.href = getDataPostulant.vacancyLink;
  };

  return (
    <Modal setShowModal={deny} handleClosedModal={() => {}}>
      <HrTitleContainer padding='32px 32px 16px 32px' gap='4px'>
        <span style={{ color: darkMode ? 'white' : '' }} className='hr-Checkmark' />
        <HrTitleH4 color={darkMode ? 'white' : ''}>
          {isConfidentialVacancy
            ? t('postulant.modal.legal_consent.title_confidential')
            : t('postulant.modal.legal_consent.title', { accountName: getDataPostulant.portalName })}
        </HrTitleH4>
      </HrTitleContainer>
      <HrFlexContainer padding='32px 32px 0 32px'>
        <LegalText
          backgroundColor={darkMode ? 'transparent' : HrlightestGrey}
          borderWidth='1px'
          borderColor={darkMode ? '#767676' : HrlighterGrey}
          padding='16px'
          borderRadius='6px'
          width='100%'
        >
          <HrParagraph color={darkMode ? 'white' : ''}>
            AUTORIZACIÓN DE USO DE DATOS PERSONALES DE POSTULANTES. CAJA MUNICIPAL DE AHORRO Y
            CRÉDITO PIURA S.A.C (en adelante, &quot;CMAC PIURA&quot;) identificada con RUC N° 20113604248 y
            con domicilio en Jirón Ayacucho N° 353 - Piura; en cumplimiento de la Ley 29733 Ley de
            Protección de Datos Personales (en adelante, LPDP) y su Reglamento aprobado por Decreto
            Supremo 016-2024-JUS (en adelante, Reglamento), informa que, al continuar, será redirigido a
            una plataforma externa (Hiring Room) para gestionar su postulación laboral y, en consecuencia,
            solicita su autorización para el tratamiento de sus datos personales.
          </HrParagraph>

          <LegalHeading color={darkMode ? 'white' : ''}>I. DE LOS DATOS PERSONALES</LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            CMAC PIURA tratará sus datos personales conforme a la siguiente clasificación:
          </HrParagraph>
          <LegalBulletList>
            <HrParagraph as='li' color={darkMode ? 'white' : ''}>
              Datos de carácter identificativo: nombres y apellidos, número de DNI, número de pasaporte,
              carné de extranjería, dirección de domicilio, teléfono, dirección de correo electrónico, imagen,
              voz, firma.
            </HrParagraph>
            <HrParagraph as='li' color={darkMode ? 'white' : ''}>
              Datos de características personales: estado civil, fecha de nacimiento, nacionalidad, sexo,
              profesión, edad, datos académicos, antecedentes penales, policiales y judiciales.
            </HrParagraph>
            <HrParagraph as='li' color={darkMode ? 'white' : ''}>
              Datos económicos-financieros y de seguros: créditos, préstamos, avales, datos bancarios,
              historial de créditos, información tributaria, seguros, tarjetas de crédito, bienes patrimoniales,
              hipotecas, deudas.
            </HrParagraph>
            <HrParagraph as='li' color={darkMode ? 'white' : ''}>
              Datos sensibles: información relativa a la salud física o mental, ingresos económicos y otros
              (datos psicométricos).
            </HrParagraph>
          </LegalBulletList>

          <LegalHeading color={darkMode ? 'white' : ''}>II. DEL BANCO DE DATOS PERSONALES</LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Los datos personales serán almacenados en el banco de datos personales denominado
            &quot;RECURSOS HUMANOS&quot;, con Código de Registro N° RNPDP-PJP N° 00597 de titularidad de CMAC
            PIURA.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            CMAC PIURA se compromete a implementar las medidas de seguridad adecuadas, tanto técnicas
            como organizativas, para proteger la confidencialidad e integridad de los datos personales del
            postulante, evitando el acceso no autorizado, alteración, pérdida o divulgación indebida de la
            información.
          </HrParagraph>

          <LegalHeading color={darkMode ? 'white' : ''}>III. DE LA FINALIDAD DEL TRATAMIENTO</LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Los datos personales serán tratados con la finalidad exclusiva de realizar las actividades
            relacionadas con el proceso de selección de personal en CMAC PIURA, lo que implica utilizar los
            datos para evaluar y seleccionar candidatos para la vacante disponible en CMAC PIURA.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Se informa de manera expresa que, para realizar los procesos de selección de personal, CMAC
            PIURA podrá realizar elaboración de perfiles, por medios automatizados, a efectos de evaluar si el
            candidato cumple con el perfil requerido.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            El tratamiento de los datos personales realizado por CMAC PIURA no se extenderá a otra
            finalidad que no haya sido la establecida de manera inequívoca como tal en el presente
            documento, excluyendo los casos de actividades de valor histórico, estadístico o científico cuando
            se utilice un procedimiento de disociación o anonimización; o sea necesario para el cumplimiento
            de obligaciones legales.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            En los casos en que el postulante se niegue a que CMAC PIURA realice el tratamiento de sus
            datos personales para la finalidad anteriormente mencionada, se deja constancia que no podrá
            continuar en el proceso de selección.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Por otro lado, en caso el postulante brinde su autorización expresa, CMAC PIURA podrá realizar
            el tratamiento de sus datos personales, para las siguientes finalidades adicionales:
          </HrParagraph>
          <LegalBulletList>
            <HrParagraph as='li' color={darkMode ? 'white' : ''}>
              Contactarle para invitarlo a participar en otros procesos de selección, distintos al que motivó
              su postulación a la fecha de aceptación del presente documento.
            </HrParagraph>
          </LegalBulletList>

          <LegalHeading color={darkMode ? 'white' : ''}>IV. DEL EJERCICIO DE LOS DERECHOS ARCO</LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            El titular de los datos personales puede ejercer sus derechos de acceso, rectificación,
            cancelación, oposición, portabilidad, entre otros, en los términos dispuestos en la Ley N° 29733 y
            su Reglamento, aprobado por Decreto Supremo N° 016-2024-JUS, a través de cualquier medio que
            permita acreditar el envío y la recepción de la solicitud a la dirección de domicilio de cualquier
            agencia de CMAC PIURA listadas en la página web{' '}
            <LegalLink href='https://www.cajapiura.pe/ubicanos' target='_blank' rel='noreferrer'>
              https://www.cajapiura.pe/ubicanos
            </LegalLink>{' '}
            y/o por medio de correo electrónico dirigido a:{' '}
            <LegalLink href='mailto:datospersonales@cajapiura.pe'>datospersonales@cajapiura.pe</LegalLink>,
            especificando mis nombres y DNI. Se podrán establecer otros canales para tramitar estas
            solicitudes, lo que será informado oportunamente por CMAC PIURA.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            De considerar que no ha sido atendido en el ejercicio de sus derechos puede presentar una
            reclamación ante la Autoridad Nacional de Protección de Datos Personales, dirigiéndose a la Mesa
            de Partes del Ministerio de Justicia y Derechos Humanos.
          </HrParagraph>

          <LegalHeading color={darkMode ? 'white' : ''}>V. DE LA TRANSFERENCIA DE DATOS PERSONALES</LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            CMAC PIURA me informa que podrá transferir mis datos personales dentro del territorio nacional o
            fuera de este, por cualquier medio, a sus subsidiarias, franquicias, socios comerciales, a sus
            proveedores de servicios, sean personas naturales o jurídicas, peruanas o extranjeras, con el
            objeto de realizar el tratamiento de mis datos personales únicamente para las finalidades
            descritas y/o consentidas en la sección II del presente documento. La lista de destinatarios de los
            datos personales puede ser consultada ingresando a los siguientes enlaces:{' '}
            <LegalLink
              href='https://cajapiura-img-prod-g4bhdmdxf3aue7g2.a02.azurefd.net/production/documents/cms/proveedores-df970252-5c69-4c60-acb6-8ff8ce73278e.pdf'
              target='_blank'
              rel='noreferrer'
            >
              Relación de proveedores (1)
            </LegalLink>{' '}
            y{' '}
            <LegalLink
              href='https://www.cajapiura.pe/_files/PDFs/Protecci%C3%B3n-Datos-Personales/2024/RELACI%C3%93N%20DE%20PROVEEDORES%20CON%20ACCESO%20A%20DATOS%20PERSONALES%202024.v3.pdf'
              target='_blank'
              rel='noreferrer'
            >
              Relación de proveedores (2)
            </LegalLink>
            .
          </HrParagraph>

          <LegalHeading color={darkMode ? 'white' : ''}>
            VI. DEL PLAZO DEL TRATAMIENTO DE DATOS PERSONALES
          </LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Los datos personales obtenidos durante el proceso de reclutamiento y selección de personal
            realizado por CMAC PIURA serán conservados durante el tiempo en el que se desarrolle el
            presente proceso y durante el tiempo necesario para cumplir con las finalidades descritas en el
            presente documento.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            En caso el postulante sea elegido para desempeñar algún puesto dentro de la empresa, CMAC
            PIURA podrá tratar sus datos personales a efectos de iniciar las actuaciones pertinentes para su
            incorporación como trabajador.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            En caso el postulante no sea seleccionado para el puesto solicitado, los datos serán conservados
            durante un periodo máximo de seis (06) meses, salvo que el postulante brinde su autorización
            expresa para ser contactado en futuras convocatorias de empleo similares de CMAC PIURA, para
            cuyo caso, el tratamiento de sus datos personales se efectuará hasta que revoque su
            consentimiento para ello.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Una vez transcurrido los plazos mencionados, los datos personales serán eliminados o
            anonimizados, salvo que exista alguna obligación legal que requiera su conservación durante un
            periodo más largo.
          </HrParagraph>

          <LegalHeading color={darkMode ? 'white' : ''}>
            VII. CONFIDENCIALIDAD EN EL TRATAMIENTO DE DATOS PERSONALES
          </LegalHeading>
          <HrParagraph color={darkMode ? 'white' : ''}>
            Con el objeto de evitar la pérdida, mal uso, alteración, acceso no autorizado y robo de los datos
            personales o información confidencial facilitados por titulares de datos personales, CMAC PIURA
            ha adoptado los niveles de seguridad y de protección de datos personales legalmente requeridos, y
            ha instalado todos los medios y medidas técnicas a su alcance.
          </HrParagraph>
          <HrParagraph color={darkMode ? 'white' : ''}>
            En este sentido, luego de que he leído los apartados anteriores, otorgo mi consentimiento para
            que CMAC PIURA realice el tratamiento de mis datos personales para los fines correspondientes.
            De no aceptar el presente formato de consentimiento, CMAC PIURA no podrá tratar mis datos
            personales para los referidos fines.
          </HrParagraph>
        </LegalText>
      </HrFlexContainer>
      <HrFlexContainer padding='32px 32px 0 32px'>
        <Checkbox
          id='legalConsentAccept'
          name='legalConsentAccept'
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          label={t('postulant.modal.legal_consent.checkbox_label')}
          darkMode={darkMode}
        />
      </HrFlexContainer>
      <HrFlexContainer padding='32px' justifyContent='flex-end' gap='8px'>
        <HrButtons category='cancel' size='medium' onClick={deny}>
          {t('postulant.modal.legal_consent.deny')}
        </HrButtons>
        <HrButtons category='primary' size='medium' disabled={!accepted} onClick={onAuthorize}>
          {t('postulant.modal.legal_consent.authorize')}
        </HrButtons>
      </HrFlexContainer>
    </Modal>
  );
};
